<template>
    <!-- portalled to Dash.vue's top-level target (disabled -> renders in place for the inline
         Settings embed): several hosts (post creator toolbar, media editor toolbar...) sit
         under an ancestor with its own CSS transform, which turns position:fixed into "fixed
         to that ancestor" instead of the viewport. Portalling out is what actually centers
         this on the real viewport rather than the nearest transformed box.
         The content itself is NOT a bespoke design: it's built from the exact same
         .form-overlay/.select-* classes the Variable/Mention/Hashtag popups use (see
         formOverlay.scss's &.select-template block) - same card, same .input/.item recipes,
         .btns floating outside the card. The action (apply / create / update) is chosen
         beforehand from the host's TemplateTrigger dropdown - no switching inside the popup. -->
    <portal to="dash" :disabled="inline">
        <div class="tp-backdrop" v-if="!inline"></div>
        <div class="tp-anchor" :class="{ 'tp-inline': inline }">
            <div class="form-overlay select-template" :class="{ 'tp-inline': inline }">
                <div class="tp-header" v-if="mode === 'browse'">
                    <p class="tp-title">{{ $t(`dash.templates.header.${action}.title`) }}</p>
                    <p class="tp-hint">{{ headerHint }}</p>
                </div>

                <!-- list: search + folder tree, sharing one drag group so moving an item
                     between folders (or to/from the root) and positioning it happen in a single
                     drag gesture - same idea as selectVariable's steep1 list, extended with
                     folders since this picker (unlike Variable) needs user-organizable storage.
                     Also step 1 of 'update' (pick the template to overwrite). -->
                <div class="steep1" v-if="showList">
                    <div class="content browseTemplate">
                        <div class="type-tabs" v-if="typeTabs.length > 1">
                            <div
                                v-for="tab in typeTabs"
                                :key="tab"
                                class="type-tab"
                                :class="{ active: activeType === tab }"
                                @click="switchType(tab)"
                            >{{ $t(`dash.templates.type.${tab}`) }}</div>
                        </div>
                        <p class="type-hint" v-if="typeTabs.length > 1">{{ $t(`dash.templates.type-hint.${activeType}`) }}</p>

                        <div class="input">
                            <i class="fa-regular fa-magnifying-glass"></i>
                            <input
                                type="text"
                                v-model="search"
                                :placeholder="$t('dash.templates.search-placeholder')"
                                @click.stop
                            >
                        </div>

                        <p class="save-blocked" v-if="action === 'update' && saveBlockedReason">{{ saveBlockedReason }}</p>

                        <div class="list-item hide-scrollbar" v-if="!search">
                            <draggable
                                :list="localRootTemplates"
                                v-bind="itemDragOptions"
                                tag="div"
                                class="drop-zone"
                                @change="evt => onFolderListChange(null, evt)"
                            >
                                <div class="item" :class="{ selected: selectedId === template.id }" v-for="template in localRootTemplates" :key="template.id" @click="onItemClick(template)">
                                    <i class="fa-solid fa-grip-vertical drag-handle" @click.stop></i>

                                    <div class="icon">
                                        <slot name="preview" :template="template">
                                            <TemplatePreview :template="template" :show-post-preview="showPostPreview" />
                                        </slot>
                                    </div>

                                    <div class="info">
                                        <input
                                            v-if="renamingKind === 'template' && renamingId === template.id"
                                            v-model="renamingValue"
                                            class="rename-input"
                                            @click.stop
                                            @keyup.enter="submitRename"
                                            @blur="submitRename"
                                        >
                                        <template v-else>
                                            <p class="name">{{ template.name }}</p>
                                            <p v-if="template.source" class="source-badge">{{ $t(`dash.templates.source.${template.source}`) }}</p>
                                            <p v-if="inline && rowExcerpt(template)" class="excerpt">{{ rowExcerpt(template) }}</p>
                                        </template>
                                    </div>


                                    <TemplateItemMenu
                                        @click.native.stop
                                        :folders="localRootFolders"
                                        :currentFolderId="null"
                                        @rename="startRename('template', template)"
                                        @move="targetId => moveTemplate(template, targetId)"
                                        @delete="deleteTemplate_(template)"
                                    />
                                </div>
                            </draggable>

                            <draggable
                                v-model="localRootFolders"
                                v-bind="folderDragOptions"
                                tag="div"
                                @end="onReorderFolders"
                            >
                                <div v-for="folder in localRootFolders" :key="folder.id">
                                    <div class="item" @click="toggleFolder(folder.id)">
                                        <i class="fa-solid fa-grip-vertical drag-handle" @click.stop></i>
                                        <div class="icon folder-icon">
                                            <i class="fa-solid" :class="isExpanded(folder.id) ? 'fa-folder-open' : 'fa-folder'"></i>
                                        </div>

                                        <div class="info">
                                            <input
                                                v-if="renamingKind === 'folder' && renamingId === folder.id"
                                                v-model="renamingValue"
                                                class="rename-input"
                                                @click.stop
                                                @keyup.enter="submitRename"
                                                @blur="submitRename"
                                            >
                                            <p v-else class="name">{{ folder.name }}</p>
                                        </div>

                                        <TemplateItemMenu
                                            @click.native.stop
                                            :movable="false"
                                            @rename="startRename('folder', folder)"
                                            @delete="deleteFolder(folder)"
                                        />
                                    </div>

                                    <draggable
                                        v-if="isExpanded(folder.id)"
                                        :list="localFolderTemplates[folder.id] || []"
                                        v-bind="itemDragOptions"
                                        tag="div"
                                        class="drop-zone drop-zone-nested"
                                        @change="evt => onFolderListChange(folder.id, evt)"
                                    >
                                        <div class="item" :class="{ selected: selectedId === template.id }" v-for="template in (localFolderTemplates[folder.id] || [])" :key="template.id" @click="onItemClick(template)">
                                            <i class="fa-solid fa-grip-vertical drag-handle" @click.stop></i>

                                            <div class="icon">
                                                <slot name="preview" :template="template">
                                                    <TemplatePreview :template="template" :show-post-preview="showPostPreview" />
                                                </slot>
                                            </div>

                                            <div class="info">
                                                <input
                                                    v-if="renamingKind === 'template' && renamingId === template.id"
                                                    v-model="renamingValue"
                                                    class="rename-input"
                                                    @click.stop
                                                    @keyup.enter="submitRename"
                                                    @blur="submitRename"
                                                >
                                                <template v-else>
                                                    <p class="name">{{ template.name }}</p>
                                                    <p v-if="template.source" class="source-badge">{{ $t(`dash.templates.source.${template.source}`) }}</p>
                                                    <p v-if="inline && rowExcerpt(template)" class="excerpt">{{ rowExcerpt(template) }}</p>
                                                </template>
                                            </div>


                                            <TemplateItemMenu
                                                @click.native.stop
                                                :folders="foldersExcluding(folder.id)"
                                                :currentFolderId="folder.id"
                                                @rename="startRename('template', template)"
                                                @move="targetId => moveTemplate(template, targetId)"
                                                @delete="deleteTemplate_(template)"
                                            />
                                        </div>

                                        <p class="empty-folder" v-if="!(localFolderTemplates[folder.id] || []).length">
                                            {{ $t('dash.templates.empty-folder') }}
                                        </p>
                                    </draggable>
                                </div>
                            </draggable>

                            <p class="no-result" v-if="!localRootTemplates.length && !localRootFolders.length">
                                {{ mode === 'browse' ? $t(`dash.templates.empty-${action}`) : $t('dash.templates.no-result') }}
                            </p>
                        </div>

                        <div class="list-item hide-scrollbar" v-else>
                            <div class="item" :class="{ selected: selectedId === template.id }" v-for="template in searchResults" :key="template.id" @click="onItemClick(template)">
                                <div class="icon">
                                    <slot name="preview" :template="template">
                                        <TemplatePreview :template="template" :show-post-preview="showPostPreview" />
                                    </slot>
                                </div>
                                <div class="info">
                                    <p class="name">{{ template.name }}</p>
                                    <p v-if="template.source" class="source-badge">{{ $t(`dash.templates.source.${template.source}`) }}</p>
                                    <p v-if="inline && rowExcerpt(template)" class="excerpt">{{ rowExcerpt(template) }}</p>
                                </div>
                            </div>
                            <p class="no-result" v-if="!searchResults.length">{{ $t('dash.templates.no-result') }}</p>
                        </div>

                        <template v-if="!search">
                            <div class="create-folder-link" @click="startCreateFolder" v-if="!creatingFolder">
                                <i class="fa-regular fa-folder-plus"></i>
                                {{ $t('dash.templates.new-folder') }}
                            </div>
                            <div class="create-folder-form" v-else>
                                <div class="input">
                                    <input
                                        type="text"
                                        v-model="newFolderName"
                                        :placeholder="$t('dash.templates.folder-name-placeholder')"
                                        @click.stop
                                        @keyup.enter="submitCreateFolder"
                                    >
                                </div>
                                <div class="form-actions">
                                    <div class="btn-secondary" @click="creatingFolder = false">{{ $t('dash.button.cancel') }}</div>
                                    <div class="btn-secondary" :class="{ disabled: !newFolderName.trim() }" @click="submitCreateFolder">{{ $t('dash.button.add') }}</div>
                                </div>
                            </div>
                        </template>

                        <router-link
                            v-if="mode !== 'manage'"
                            to="/dash/settings/templates"
                            target="_blank"
                            class="manage-link-text"
                        >{{ $t('dash.templates.manage-link') }}</router-link>
                    </div>
                </div>

                <!-- create form, or step 2 of 'update' (editingTemplateId set) - fields only, no
                     list visible at the same time (avoids the "everything at once" density of
                     the very first version) -->
                <div class="steep1" v-else>
                    <div class="content createTemplate">
                        <template v-if="action === 'create'">
                            <div class="type-tabs" v-if="typeTabs.length > 1">
                                <div
                                    v-for="tab in typeTabs"
                                    :key="tab"
                                    class="type-tab"
                                    :class="{ active: activeType === tab }"
                                    @click="switchType(tab)"
                                >{{ $t(`dash.templates.type.${tab}`) }}</div>
                            </div>
                            <p class="type-hint" v-if="typeTabs.length > 1">{{ $t(`dash.templates.type-hint.${activeType}`) }}</p>
                        </template>
                        <div class="back-to-list" v-else @click="backToList">
                            <i class="fa-regular fa-arrow-left"></i>
                            {{ $t('dash.templates.back-to-list') }}
                        </div>

                        <p class="save-blocked" v-if="saveBlockedReason">{{ saveBlockedReason }}</p>

                        <template v-if="activeType === 'affix'">
                            <div class="input input-textarea">
                                <textarea
                                    ref="prefixTextarea"
                                    v-model="affixPrefix"
                                    rows="2"
                                    :placeholder="$t('dash.templates.affix-prefix-placeholder')"
                                    @click.stop
                                ></textarea>
                                <i class="fa-regular fa-face-laugh emoji-trigger" @click.stop="toggleEmojiPicker('prefix')"></i>
                                <Picker
                                    v-if="openEmojiPickerFor === 'prefix'"
                                    :data="emojiIndex"
                                    class="affix-emoji-picker"
                                    :color="'#b64985'"
                                    @select="emoji => insertEmoji('prefix', emoji)"
                                    @click.native.stop
                                />
                            </div>
                            <div class="input input-textarea">
                                <textarea
                                    ref="suffixTextarea"
                                    v-model="affixSuffix"
                                    rows="2"
                                    :placeholder="$t('dash.templates.affix-suffix-placeholder')"
                                    @click.stop
                                ></textarea>
                                <i class="fa-regular fa-face-laugh emoji-trigger" @click.stop="toggleEmojiPicker('suffix')"></i>
                                <Picker
                                    v-if="openEmojiPickerFor === 'suffix'"
                                    :data="emojiIndex"
                                    class="affix-emoji-picker"
                                    :color="'#b64985'"
                                    @select="emoji => insertEmoji('suffix', emoji)"
                                    @click.native.stop
                                />
                            </div>
                        </template>

                        <div class="input">
                            <select v-model="saveTargetFolderId" @click.stop>
                                <option :value="null">{{ $t('dash.templates.root') }}</option>
                                <option v-for="folder in localRootFolders" :key="folder.id" :value="folder.id">{{ folder.name }}</option>
                            </select>
                        </div>

                        <div class="input">
                            <input
                                type="text"
                                v-model="newTemplateName"
                                :placeholder="$t('dash.templates.name-placeholder')"
                                @click.stop
                                @keyup.enter="submitSave"
                            >
                        </div>
                    </div>
                </div>

                <div class="btns" v-if="mode === 'browse'">
                    <div class="btn-secondary" @click="close">{{ $t('dash.button.close') }}</div>
                    <div
                        v-if="!showList"
                        class="btn-secondary"
                        :class="{ disabled: !canSubmitSave }"
                        @click="submitSave"
                    >{{ action === 'update' ? $t('dash.templates.update-button') : $t('dash.templates.create-button') }}</div>
                </div>
            </div>
        </div>
    </portal>
</template>

<script>
import draggable from 'vuedraggable'
import { Picker, EmojiIndex } from 'emoji-mart-vue-fast'
import emojiData from '@/constants/emoji-mart-vue-fast/all.json'
import TemplatesMixin from '@/components/mixins/Templates.vue'
import TemplateFoldersMixin from '@/components/mixins/TemplateFolders.vue'
import TemplatePreview from './TemplatePreview.vue'
import TemplateItemMenu from './TemplateItemMenu.vue'

export default {
    name: 'TemplatePicker',

    components: { draggable, TemplatePreview, TemplateItemMenu, Picker },

    mixins: [TemplatesMixin, TemplateFoldersMixin],

    props: {
        type: {
            type: String,
            required: true,
            validator: v => ['post', 'text', 'media', 'affix'].includes(v)
        },
        secondaryTypes: {
            type: Array,
            default: () => []
        },
        mode: {
            type: String,
            default: 'browse',
            // 'browse': popover whose content depends on `action`. 'manage': embedded full-page (see `inline`) - picking a
            // template emits 'select' instead of applying it, for the management page's
            // content editor; has no save/update tab of its own (list only, no .btns).
            validator: v => ['browse', 'manage'].includes(v)
        },
        // 'browse' mode only, chosen from TemplateTrigger before opening: 'apply' (list, click
        // applies), 'create' (form), 'update' (list, click opens that template's form)
        action: {
            type: String,
            default: 'apply',
            validator: v => ['apply', 'create', 'update'].includes(v)
        },
        // { data, source } to persist/overwrite with - when provided in 'browse' mode, the
        // create step can actually submit (see saveBlockedReason) and existing rows get an
        // "update with current content" pencil action
        saveData: {
            type: Object,
            default: null
        },
        // renders as a plain embedded block instead of a floating popover (management page)
        inline: {
            type: Boolean,
            default: false
        },
        showPostPreview: {
            type: Boolean,
            default: false
        },
        // shown in the create step instead of the generic message when saveData is null (e.g.
        // "the file must finish uploading first") - purely informational, see saveBlockedReason
        saveUnavailableText: {
            type: String,
            default: ''
        },
        // id of the template currently open in the management page's detail panel (mode
        // 'manage' only) - highlighted the same way a row is on hover, so it stays visible
        // which one you're editing even after the mouse moves away.
        selectedId: {
            type: String,
            default: null
        }
    },

    data() {
        return {
            activeType: this.type,
            editingTemplateId: null,
            editingTemplateName: '',
            search: '',
            expandedFolderIds: [],
            creatingFolder: false,
            newFolderName: '',
            newTemplateName: '',
            affixPrefix: '',
            affixSuffix: '',
            saveTargetFolderId: null,
            savingInProgress: false,
            renamingKind: null,
            renamingId: null,
            renamingValue: '',
            // 'prefix' | 'suffix' | null - which affix field's emoji flyout is open, if any
            openEmojiPickerFor: null,
            emojiIndex: new EmojiIndex(emojiData, {
                recentLength: 18,
                recent: ['tada', 'sparkles', 'rocket', 'confetti_ball', 'fire', 'heart', 'speech_balloon', 'heart_eyes', 'raised_hands', 'two_hearts', 'bulb', 'brain', 'loudspeaker', 'star2', 'wrench', 'hammer_and_wrench', 'calendar', 'spiral_calendar_pad'],
            }),
            // local mirrors of the store, genuinely mutated in place by vuedraggable
            // (a fresh array from a getter would be discarded on every drag frame)
            localRootFolders: [],
            localRootTemplates: [],
            localFolderTemplates: {}
        }
    },

    computed: {
        typeTabs() {
            return [this.type, ...this.secondaryTypes]
        },

        showList() {
            return this.mode === 'manage' || this.action === 'apply' || (this.action === 'update' && !this.editingTemplateId)
        },

        headerHint() {
            const affix = this.activeType === 'affix' ? '-affix' : ''
            if (this.action === 'update' && this.editingTemplateId) {
                return this.$t(`dash.templates.header.update.form-hint${affix}`, { name: this.editingTemplateName })
            }
            return this.$t(`dash.templates.header.${this.action}.hint${this.action === 'apply' ? '' : affix}`)
        },

        // Why the create step's submit is disabled right now, or '' when it isn't. Affix never
        // needs saveData (its content is typed fresh in the form), everything else does.
        saveBlockedReason() {
            if (this.mode !== 'browse') return ''
            if (this.activeType === 'affix') return ''
            if (this.saveData) return ''
            return this.saveUnavailableText || this.$t('dash.templates.save-unavailable-generic')
        },

        canSubmitSave() {
            if (!this.newTemplateName.trim() || this.savingInProgress) return false
            if (this.activeType === 'affix') return !!(this.affixPrefix.trim() || this.affixSuffix.trim())
            return !!this.saveData
        },

        itemDragOptions() {
            return { animation: 200, group: 'template-items', handle: '.drag-handle', ghostClass: 'tp-ghost' }
        },

        folderDragOptions() {
            return { animation: 200, group: 'template-folders', handle: '.drag-handle', ghostClass: 'tp-ghost' }
        },

        storeFolders() {
            return this.$store.getters['templateFolders/getFoldersByType'](this.activeType)
        },

        storeTemplates() {
            return this.$store.getters['templates/getTemplatesByType'](this.activeType)
        },

        searchResults() {
            const q = this.search.trim().toLowerCase()
            if (!q) return []
            return this.storeTemplates.filter(t => t.name.toLowerCase().includes(q))
        }
    },

    watch: {
        storeFolders: {
            immediate: true,
            handler(list) {
                this.localRootFolders = [...list].sort((a, b) => a.position - b.position)
            }
        },

        storeTemplates: {
            immediate: true,
            handler(list) {
                this.rebuildLocalTemplates(list)
            }
        },

        // auto-grow covers typing, emoji insertion and pre-filling from an existing template
        // (openEditForm) alike, since all three just assign these two data properties
        affixPrefix() {
            this.$nextTick(() => this.autoGrowTextarea('prefixTextarea'))
        },

        affixSuffix() {
            this.$nextTick(() => this.autoGrowTextarea('suffixTextarea'))
        }
    },

    mounted() {
        this.typeTabs.forEach(t => {
            this.$store.dispatch('templates/ensureTypeLoaded', t)
            this.$store.dispatch('templateFolders/ensureTypeLoaded', t)
        })
    },

    methods: {
        rebuildLocalTemplates(list) {
            const byFolder = { root: [] }
            this.localRootFolders.forEach(f => { byFolder[f.id] = [] })

            list.forEach(t => {
                const key = t.folder_id || 'root'
                if (!byFolder[key]) byFolder[key] = []
                byFolder[key].push(t)
            })

            Object.keys(byFolder).forEach(k => byFolder[k].sort((a, b) => a.position - b.position))

            this.localRootTemplates = byFolder.root
            delete byFolder.root
            this.localFolderTemplates = byFolder
        },

        // templates can be moved to any root folder (depth is capped at one level, so no cycle risk);
        // still exclude the folder they're already in from the "move to" list
        foldersExcluding(folderId) {
            return this.localRootFolders.filter(f => f.id !== folderId)
        },

        isExpanded(folderId) {
            return this.expandedFolderIds.includes(folderId)
        },

        toggleFolder(folderId) {
            this.expandedFolderIds = this.isExpanded(folderId)
                ? this.expandedFolderIds.filter(id => id !== folderId)
                : [...this.expandedFolderIds, folderId]
        },

        switchType(t) {
            this.activeType = t
            this.search = ''
            this.expandedFolderIds = []
            this.editingTemplateId = null
        },

        onItemClick(template) {
            if (this.mode === 'manage') {
                this.$emit('select', template)
                return
            }
            if (this.action === 'update') {
                this.openEditForm(template)
                return
            }
            this.$emit('apply', template)
            this.close()
        },

        // 'update' step 2: form pre-filled with that template's name/folder so submitSave()
        // overwrites it instead of creating a new one
        openEditForm(template) {
            this.editingTemplateId = template.id
            this.newTemplateName = template.name
            this.saveTargetFolderId = template.folder_id || null
            if (this.activeType === 'affix') {
                this.affixPrefix = (template.data && template.data.prefix) || ''
                this.affixSuffix = (template.data && template.data.suffix) || ''
            }
            this.editingTemplateName = template.name
        },

        backToList() {
            this.editingTemplateId = null
            this.editingTemplateName = ''
            this.newTemplateName = ''
            this.affixPrefix = ''
            this.affixSuffix = ''
            this.saveTargetFolderId = null
        },

        toggleEmojiPicker(field) {
            this.openEmojiPickerFor = this.openEmojiPickerFor === field ? null : field
        },

        // inline (management page) only - a plain-text preview of what a row actually contains,
        // so browsing the list already tells most of the story instead of needing to open every
        // row in the detail panel just to see its content. media/post keep the icon-only preview
        // (a thumbnail already carries their content better than text would).
        rowExcerpt(template) {
            const data = template.data || {}
            if (template.type === 'text') return data.text || ''
            if (template.type === 'affix') {
                const prefix = (data.prefix || '').trim()
                const suffix = (data.suffix || '').trim()
                if (!prefix && !suffix) return ''
                return prefix && suffix ? `${prefix} … ${suffix}` : (prefix || suffix)
            }
            return ''
        },

        // resets to 'auto' first so shrinking (deleting a line) is picked up too, not just growth
        autoGrowTextarea(refName) {
            const el = this.$refs[refName]
            if (!el) return
            el.style.height = 'auto'
            el.style.height = `${el.scrollHeight}px`
        },

        // prefix/suffix are plain <textarea>s (not the TipTap editor the main text fields use),
        // so emoji insertion goes through selectionStart/selectionEnd directly rather than
        // editor.chain().insertContent(...)
        insertEmoji(field, emoji) {
            const refName = field === 'prefix' ? 'prefixTextarea' : 'suffixTextarea'
            const el = this.$refs[refName]
            const current = field === 'prefix' ? this.affixPrefix : this.affixSuffix
            const start = el ? el.selectionStart : current.length
            const end = el ? el.selectionEnd : current.length
            const next = current.slice(0, start) + emoji.native + current.slice(end)

            if (field === 'prefix') this.affixPrefix = next
            else this.affixSuffix = next

            this.openEmojiPickerFor = null

            this.$nextTick(() => {
                if (!el) return
                const pos = start + emoji.native.length
                el.focus()
                el.setSelectionRange(pos, pos)
            })
        },

        async submitSave() {
            if (!this.canSubmitSave) return

            const name = this.newTemplateName.trim()
            const isAffix = this.activeType === 'affix'
            // unlike every other trimmed field, prefix/suffix keep their leading/trailing
            // whitespace as typed - a leading/trailing line break is often the whole point
            // (e.g. a blank line before the post's own text)
            const data = isAffix
                ? { prefix: this.affixPrefix, suffix: this.affixSuffix }
                : this.saveData.data

            this.savingInProgress = true

            let res
            if (this.editingTemplateId) {
                res = await this.updateTemplate({
                    id: this.editingTemplateId,
                    name,
                    folder_id: this.saveTargetFolderId,
                    data,
                    source: isAffix ? undefined : (this.saveData.source || undefined)
                })
            } else {
                const targetList = this.saveTargetFolderId ? (this.localFolderTemplates[this.saveTargetFolderId] || []) : this.localRootTemplates
                res = await this.newTemplate({
                    type: this.activeType,
                    folder_id: this.saveTargetFolderId,
                    name,
                    data,
                    source: isAffix ? null : (this.saveData.source || null),
                    position: targetList.length
                })
            }

            this.savingInProgress = false

            if (res && res.body && res.body.success) {
                await this.$store.dispatch('templates/fetchTemplates', this.activeType)
                this.$emit('saved', res.body.template)
                this.close()
            }
        },

        startCreateFolder() {
            this.creatingFolder = true
            this.newFolderName = ''
        },

        async submitCreateFolder() {
            const name = this.newFolderName.trim()
            if (!name) return

            const res = await this.newTemplateFolder({
                type: this.activeType,
                parent_id: null,
                name,
                position: this.localRootFolders.length
            })

            if (res && res.body && res.body.success) {
                await this.$store.dispatch('templateFolders/fetchFolders', this.activeType)
            }

            this.creatingFolder = false
            this.newFolderName = ''
        },

        startRename(kind, item) {
            this.renamingKind = kind
            this.renamingId = item.id
            this.renamingValue = item.name
        },

        async submitRename() {
            const name = this.renamingValue.trim()
            const { renamingKind, renamingId } = this

            this.renamingKind = null
            this.renamingId = null

            if (!name || !renamingId) return

            if (renamingKind === 'folder') {
                await this.updateTemplateFolder({ id: renamingId, name })
                await this.$store.dispatch('templateFolders/fetchFolders', this.activeType)
            } else {
                await this.updateTemplate({ id: renamingId, name })
                await this.$store.dispatch('templates/fetchTemplates', this.activeType)
            }
        },

        // explicit "move to" from the item menu — an alternative to dragging, same effect:
        // append at the end of the target list, then persist that list's full order
        async moveTemplate(template, targetId) {
            const targetKey = targetId || 'root'
            const target = targetKey === 'root' ? this.localRootTemplates : (this.localFolderTemplates[targetId] || [])
            const orderedIds = [...target.map(t => t.id), template.id]
            await this.reorderTemplates({ folderId: targetId || null, orderedIds })
            await this.$store.dispatch('templates/fetchTemplates', this.activeType)
        },

        async deleteFolder(folder) {
            const res = await this.deleteTemplateFolder(folder.id)
            if (res && res.body && res.body.success) {
                await this.$store.dispatch('templateFolders/fetchFolders', this.activeType)
            } else if (res && res.body && res.body.error === 'dash.error.template_folder_not_empty') {
                // eslint-disable-next-line no-alert
                alert(this.$t('dash.templates.folder-not-empty'))
            }
        },

        async deleteTemplate_(template) {
            const res = await this.deleteTemplate(template.id)
            if (res && res.body && res.body.success) {
                this.$store.commit('templates/removeTemplate', template.id)
                this.$emit('deleted', template.id)
            }
        },

        async onReorderFolders() {
            const orderedIds = this.localRootFolders.map(f => f.id)
            await this.reorderTemplateFolders({ parentId: null, orderedIds })
            await this.$store.dispatch('templateFolders/fetchFolders', this.activeType)
        },

        // fires on every drop-zone touched by a drag (both the list an item left and the
        // one it entered), vuedraggable having already spliced `list` in place — so reading
        // it back here gives the exact order/folder the user just dropped into
        async onFolderListChange(folderId, evt) {
            if (!evt.added && !evt.removed && !evt.moved) return

            const list = folderId ? (this.localFolderTemplates[folderId] || []) : this.localRootTemplates
            const orderedIds = list.map(t => t.id)

            await this.reorderTemplates({ folderId, orderedIds })
            await this.$store.dispatch('templates/fetchTemplates', this.activeType)
        },

        close() {
            this.$emit('close')
        }
    }
}
</script>

<style lang="scss" scoped>
// Positioning only - all the card/list/form visuals come from the global
// .form-overlay.select-template recipe in formOverlay.scss (shared with the other popups).
.tp-backdrop {
    position: fixed;
    inset: 0;
    background: rgba(20, 16, 20, 0.55);
    z-index: 9998;
}

.tp-anchor {
    position: fixed;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    z-index: 9999;
    max-height: 85vh;

    &.tp-inline {
        position: static;
        top: auto;
        left: auto;
        transform: none;
        max-height: none;
    }
}
</style>
